import { cn } from "@/lib/utils";

// ===========================================================================
// El cuadro «DICE EL VOUCHER DEL CLIENTE» y su gemelo «ENTRÓ EN EL BANCO».
//
// Vivía dentro de la revisión guiada. Se saca aquí porque la ficha de un
// archivo necesita EL MISMO: contabilidad lo pidió tal cual —«que muestre la
// info extraída en un componente tal cual como dice revisión guiada»— y dos
// copias del mismo cuadro acaban divergiendo en el primer arreglo que solo se
// aplique a una.
// ===========================================================================

export function Lado({
  titulo,
  importe,
  filas,
  resaltar,
  etiqueta,
  origen,
  nota,
  cuenta,
  alEditar,
}: {
  titulo: string;
  importe: string;
  filas: string[][];
  resaltar?: string;
  /** Cómo se pagó: Yape, Plin, transferencia… Solo si se sabe. */
  etiqueta?: string | null;
  /** Cómo llegó la imagen: captura de pantalla o foto tomada. */
  origen?: string | null;
  /** Una línea que cambia la lectura de todo lo demás. */
  nota?: { texto: string; tono: "ok" | "warn" } | null;
  /**
   * Si la cuenta a la que entró el dinero es del grupo o no.
   *
   * Va debajo de las filas y no como una fila más porque no es un dato del
   * papel: es lo que se sabe DESPUÉS de cotejarlo con el maestro. Y porque
   * «no está registrada» hay que verlo, no leerlo de refilón.
   */
  cuenta?: { texto: string; tono: "ok" | "warn" } | null;
  /** Solo el lado del voucher se corrige: el extracto lo dice el banco. */
  alEditar?: () => void;
}) {
  return (
    <div className="rounded-lg border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
          {titulo}
        </div>
        {/* El medio de pago va arriba y no en la lista de campos: cambia CÓMO
            se leen los demás. Un Yape no trae número de operación, así que
            «N.º operación —» deja de ser una lectura fallida y pasa a ser lo
            normal. Verlo después de haber juzgado el resto no sirve. */}
        {etiqueta ? (
          <span className="rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 font-medium text-[10.5px] text-cyan-700 uppercase tracking-wide dark:text-cyan-400">
            {etiqueta}
          </span>
        ) : null}
        {/* Y cómo llegó la imagen, por el mismo motivo que el medio: una
            captura se lee casi siempre bien, y una foto de papel es donde el
            lector falla. En gris y no en color, porque acompaña al medio en vez
            de competir con él. */}
        {origen ? (
          <span className="rounded-full border px-2 py-0.5 font-medium text-[10.5px] text-muted-foreground uppercase tracking-wide">
            {origen}
          </span>
        ) : null}
        {alEditar ? (
          <button
            type="button"
            onClick={alEditar}
            className="ml-auto rounded border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted"
          >
            Corregir
          </button>
        ) : null}
      </div>
      <div className="mt-2 font-bold text-3xl tabular-nums tracking-tight">
        {importe}
      </div>
      {nota ? (
        <p
          className={cn(
            "mt-2 rounded px-2 py-1 text-[11.5px]",
            nota.tono === "ok"
              ? "bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
              : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
          )}
        >
          {nota.texto}
        </p>
      ) : null}
      <dl className="mt-4 space-y-2.5 text-[12.5px]">
        {filas.map(([k, v]) => (
          <div
            key={`${k}${v}`}
            className="flex items-baseline justify-between gap-4"
          >
            <dt className="shrink-0 text-muted-foreground">{k}</dt>
            <dd
              className={cn(
                "truncate text-right font-medium",
                // El número de operación en ancho fijo: la diferencia entre
                // 03634358 y 3634358 tiene que saltar a la vista sin contar
                // dígitos con el dedo.
                k === "N.º operación" && "font-mono text-[12px]",
                // Lo que NO coincide se resalta: es lo único que hay que mirar
                // para responder.
                resaltar === k && "rounded bg-amber-500/20 px-1.5",
              )}
              title={v}
            >
              {v}
            </dd>
          </div>
        ))}
      </dl>
      {cuenta ? (
        <p
          className={cn(
            "mt-3 rounded px-2 py-1 text-[11.5px]",
            cuenta.tono === "ok"
              ? "bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
              : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
          )}
        >
          {cuenta.texto}
        </p>
      ) : null}
    </div>
  );
}
